import { Switch, Typography } from 'antd';
import { CoffeeOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ScheduleBreakEntry, ScheduleDayEntry } from '../../../types/provider';

const { Text } = Typography;

const fmtTime = (t: string | null | undefined) => (t ? t.slice(0, 5) : '');

interface Props {
  day: ScheduleDayEntry;
  dayLabel: string;
  breaks: ScheduleBreakEntry[];
  onToggle: () => void;
  onEdit: () => void;
}

export default function DayRow({ day, dayLabel, breaks, onToggle, onEdit }: Props) {
  const { t } = useTranslation('provider');
  const accent = day.enabled ? '#3390ec' : '#9ca3af';

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 14,
        background: 'var(--bl-surface, #fff)',
        border: `1px solid ${day.enabled ? 'var(--bl-divider, #ebebeb)' : 'transparent'}`,
        padding: '10px 14px 10px 0',
        display: 'flex',
        alignItems: 'stretch',
        gap: 12,
        transition: 'background 0.2s, border 0.2s',
        opacity: day.enabled ? 1 : 0.78,
      }}
    >
      {/* Left accent bar */}
      <div
        style={{
          width: 3,
          alignSelf: 'stretch',
          borderRadius: '3px 0 0 3px',
          background: day.enabled
            ? `linear-gradient(180deg, ${accent} 0%, ${accent}80 100%)`
            : 'var(--bl-divider, #ebebeb)',
        }}
      />

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 1, paddingBottom: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Switch size="small" checked={day.enabled} onChange={onToggle} />
            <Text
              style={{
                fontSize: 14,
                fontWeight: day.enabled ? 700 : 500,
                color: day.enabled
                  ? 'var(--bl-text-strong, #0f172a)'
                  : 'var(--bl-text-muted, #9ca3af)',
                letterSpacing: day.enabled ? -0.1 : 0,
              }}
            >
              {dayLabel}
            </Text>
          </div>

          {day.enabled ? (
            <button
              type="button"
              onClick={onEdit}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: `linear-gradient(135deg, ${accent}14 0%, ${accent}26 100%)`,
                border: `1px solid ${accent}22`,
                color: accent,
                borderRadius: 9,
                padding: '4px 10px',
                fontSize: 13,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
              }}
            >
              {fmtTime(day.startTime)} – {fmtTime(day.endTime)}
              <EditOutlined style={{ fontSize: 10 }} />
            </button>
          ) : (
            <Text
              style={{
                fontSize: 12,
                color: 'var(--bl-text-muted, #9ca3af)',
                fontWeight: 500,
              }}
            >
              {t('schedule.weekly.dayOff')}
            </Text>
          )}
        </div>

        {day.enabled && breaks.length > 0 && (
          <div
            style={{
              paddingLeft: 38,
              display: 'flex',
              gap: 5,
              flexWrap: 'wrap',
            }}
          >
            {breaks.map((b) => (
              <div
                key={b.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  background: 'rgba(250,140,22,0.10)',
                  border: '1px solid rgba(250,140,22,0.18)',
                  borderRadius: 8,
                  padding: '2px 8px',
                  fontSize: 11,
                  color: '#d46b08',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <CoffeeOutlined style={{ fontSize: 9 }} />
                {fmtTime(b.startTime)}–{fmtTime(b.endTime)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
